import {
  Dispatch,
  SetStateAction,
  createContext,
  use,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { User } from '@/types/index';
import { getLocalStorageValue, localStorageKey } from '@/utils/utils';
import { fetchProfile } from '@/api/users';

interface AuthContextType {
  isLoadingProfile: boolean;
  token: string | null;
  profile: User | undefined;
  setToken: Dispatch<SetStateAction<string | null>>;
  setProfile: Dispatch<SetStateAction<User | undefined>>;
  currentUserDetails: {
    name: string;
    userId: number;
    isAdmin: boolean;
  };
}

const AuthContext = createContext<AuthContextType>({
  isLoadingProfile: false,
  token: null,
  profile: undefined,
  setToken: () => { },
  setProfile: () => { },
  currentUserDetails: { name: '', userId: 0, isAdmin: false },
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<User | undefined>();
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  const currentUserDetails = useMemo(() => ({
    name: profile?.name || '',
    userId: profile?.id || 0,
    isAdmin: false, // It used to be profile.isOrgAdmin (deprecated in the backend)
  }), [profile]);

  const providerValue = useMemo(() => ({
    isLoadingProfile,
    token,
    setToken,
    profile,
    setProfile,
    currentUserDetails,
  }), [isLoadingProfile, token, profile, currentUserDetails]);

  useEffect(() => {
    const loadToken = () => {
      const _token = getLocalStorageValue(localStorageKey.wovenToken);
      setToken(_token);
      setIsLoadingProfile(false);
    };
    loadToken();
  }, []);

  useEffect(() => {
    if (token) {
      fetchProfile().then((profile) => {
        if (profile) {
          setProfile(profile);
        }
      });
    }
  }, [token]);

  return (
    <AuthContext value={providerValue}>
      {!isLoadingProfile && children}
    </AuthContext>
  );
};

export const useAuthContext = () => use(AuthContext);
